<script setup lang="ts">
import { ref } from "vue";
import type { Order, Postcard } from "@/rvapi";
import rvapi from "@/rvapi";
import { utc2local } from "@/utils";
import config from "@/config";

const order = ref({} as Order);
const postcards = ref([] as Postcard[]);
const history = ref(false);

const searching = ref(false);
const invalid = ref(false);
const onSearch = async (e: any) => {
  searching.value = true;
  invalid.value = false;
  const result = await rvapi.getPaymentInfo(e.detail);
  if (!result || !result.order) {
    invalid.value = true;
    searching.value = false;
    return;
  }
  order.value = result.order;
  history.value = result.history;
  postcards.value = result.postcard;
  searching.value = false;
};
</script>

<template>
  <h1>Order id search</h1>
  <section>
    <div class="search">
      <pn-search-field
        name="query"
        v-on:search="onSearch"
        placeholder="123456789ABC4D"
        :loading="searching"
        label=""
      >
      </pn-search-field>
    </div>
    <p>The order id can be found printed on the back of each postcard.</p>
  </section>
  <p v-show="invalid">
    <pn-toast appearance="error" text="Could not find order"></pn-toast>
  </p>
  <section v-if="!searching && !invalid && order.orderId">
    <h3>Order {{ order.orderId }}</h3>
    <p>
      An order can have multiple postcards, and each postcard can have multiple
      receivers. This order has
      {{ postcards.reduce((p, c) => p + c.receivers.length, 0) }} receivers
      total, divided over {{ postcards.length }} postcards.
    </p>
    <p v-show="history">
      <pn-toast closable="false">
        This order is older than 90 days. No actions can be taken on this order.
        Limited information is available for this order.
      </pn-toast>
    </p>
    <dl>
      <dt>Order date</dt>
      <dd>{{ utc2local(order.orderDate) }}</dd>
      <dt>Order status</dt>
      <dd>{{ order.orderStatus }}</dd>
      <dt>Total number of cards</dt>
      <dd>{{ order.totalReceivers }}</dd>
      <dt>Platform</dt>
      <dd>{{ order.platform }}</dd>
    </dl>

    <section id="postcards" v-show="!history && postcards.length > 0">
      <h2>
        Postcards <span>({{ postcards.length }})</span>
      </h2>
      <p>
        These are the images the customer uploaded. If they look strange,
        something may have caused issues in the mobile app, or during upload.
        The images are deleted after 90 days.
      </p>
      <div
        class="postcard"
        v-for="postcard in postcards"
        :key="postcard.cardId"
      >
        <h4>Postcard #{{ postcard.postcardIndex }}</h4>
        <div class="postcard-details">
          <dl class="postcard-info">
            <dt>Created date</dt>
            <dd>{{ utc2local(postcard.createdDate) }}</dd>
            <dt>Format</dt>
            <dd>{{ postcard.format }}</dd>
          </dl>
          <a :href="postcard.cardContent?.frontImageUrl" class="front-image">
            <img
              :src="postcard.cardContent?.frontImageUrl"
              class="full-width rounded"
            />
          </a>
          <a :href="postcard.cardContent?.backImageUrl" class="front-image">
            <img
              :src="postcard.cardContent?.backImageUrl"
              class="full-width rounded"
            />
          </a>
        </div>
        <div class="zip" v-if="!history">
          <h3>
            Postcard Zipfiles <span>({{ postcard.batchIds.length }})</span>
          </h3>
          <p>
            The postcard backsides are put in groups of 10 and zipped together,
            so bigger orders have multiple backs in each zipfile
          </p>
          <ul>
            <li v-for="id in postcard.batchIds" :key="id">
              <a :href="`${config.zipBucket}/${postcard.key}/${id}.zip`">
                {{ id }}
              </a>
            </li>
          </ul>
        </div>
        <div v-if="!history" class="receivers">
          <h3>
            Receivers <span>({{ postcard.receivers.length }})</span>
          </h3>
          <p>
            The zip link for each receiver will download the zip file where that
            receiver's back can be found.
          </p>
          <pn-table bordered="true" striped="true">
            <table>
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Batch Id</th>
                  <th>Name</th>
                  <th>Name2</th>
                  <th>Address</th>
                  <th>City</th>
                  <th>Postal code</th>
                  <th>Country</th>
                  <th>Zip</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in postcard.receivers" :key="r._id">
                  <td>{{ r.status }}</td>
                  <td>{{ r.batchId }}</td>
                  <td>{{ r.name }}</td>
                  <td>{{ r.name2 }}</td>
                  <td>{{ r.address }}</td>
                  <td>{{ r.city }}</td>
                  <td>{{ r.postalCode }}</td>
                  <td>{{ r.country }} ({{ r.countryCode }})</td>
                  <td>
                    <a
                      v-show="['SENT', 'PRINTED'].includes(r.status)"
                      :href="`${config.zipBucket}/${postcard.key}/${r.batchId}.zip`"
                      >link</a
                    >
                  </td>
                </tr>
              </tbody>
            </table>
          </pn-table>
        </div>
      </div>
    </section>
  </section>
</template>

<style lang="scss" scoped>
$gray50: #f3f2f2;

.resend {
  text-align: end;
}

.postcard-details {
  display: grid;
  grid-template-columns: 1fr 1fr 3fr;
  grid-auto-flow: row dense;
  column-gap: 2rem;
}

.postcard {
  background-color: white;
  padding: 1rem;
  border-radius: 1rem;
  padding-bottom: 1rem;
  margin-bottom: 1rem;
  border: 1px solid $gray50;

  img {
    border: 1px dashed gray;
  }

  .receivers {
    grid-column: 1 / span 2;
  }

  .checkbox {
    padding: 0px;

    label {
      margin: auto;
      padding: 15px;
      display: block;
    }
  }
}

div.grid-two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 1rem;
}

.full-width {
  max-width: 300px;
  max-height: 300px;
}

.rounded {
  border-radius: 1rem;
}
</style>
